import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Bell, Check, CheckCheck, Sparkles, AlertTriangle, ShieldCheck, ClipboardCheck, MessageSquare, Star, Calendar } from "lucide-react";

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token") || ""}` });

const PRIORITY = {
  red:   { color: "#EF4444" },
  amber: { color: "#F59E0B" },
  green: { color: "#22C55E" },
};

const KIND_ICON = {
  ai_alert:               AlertTriangle,
  proposal_pending:       Sparkles,
  proposal_decided:       Check,
  checkin_missed:         ClipboardCheck,
  form_submitted:         ClipboardCheck,
  form_assigned:          ClipboardCheck,
  session_stopped:        ShieldCheck,
  community_report:       AlertTriangle,
  community_post_pending: MessageSquare,
  community_post_approved: Check,
  message:                MessageSquare,
  testimonial_request:    Star,
  appointment_booked:     Calendar,
  appointment_confirmed:  Calendar,
  appointment_declined:   Calendar,
  appointment_cancelled:  Calendar,
  appointment_rescheduled: Calendar,
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
}

export default function NotificationBell({ testidSuffix = "" }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);

  const refreshCount = useCallback(async () => {
    if (!localStorage.getItem("fl_token")) return;
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, { headers: HEADERS() });
      setCount(r.data?.unread || 0);
    } catch {}
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: 25 }, headers: HEADERS() });
      setItems(r.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  // Poll unread count every 45s while mounted (cheap head call).
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 45000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Close on outside click. Panel is portaled to document.body so we check both
  // the bell trigger (ref) and the panel (panelRef).
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadItems();
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: HEADERS() });
      setItems((s) => s.map((n) => n.id === id ? { ...n, read: true } : n));
      setCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: HEADERS() });
      setItems((s) => s.map((n) => ({ ...n, read: true })));
      setCount(0);
    } catch {}
  };

  const openItem = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) { setOpen(false); nav(n.link); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative p-1.5 rounded-md transition-colors hover:text-[color:var(--fl-orange)]"
        style={{ color: "var(--fl-muted)" }}
        aria-label="Notifications"
        data-testid={`notification-bell${testidSuffix ? "-" + testidSuffix : ""}`}
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
            style={{ background: "var(--fl-orange)", color: "#000" }}
            data-testid={`notification-badge${testidSuffix ? "-" + testidSuffix : ""}`}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed mt-2 w-80 md:w-96 fl-card overflow-hidden shadow-2xl"
          style={{
            zIndex: 9999,                                     // above everything (sidebar, modals, coach widget)
            maxHeight: "70vh",
            ...(testidSuffix === "mobile"
              ? { top: "56px", right: "12px" }                // anchor to mobile top bar
              : { top: "125px", left: "72px" }                // anchor next to desktop sidebar bell
            ),
          }}
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--fl-border)" }}>
            <div>
              <div className="fl-label">Notifications</div>
              <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{count} unread</div>
            </div>
            {count > 0 && (
              <button onClick={markAllRead} className="text-[11px] fl-btn-ghost px-2 py-1 rounded-md inline-flex items-center gap-1" data-testid="notification-read-all">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: "56vh" }}>
            {loading && <div className="p-6 text-xs text-center" style={{ color: "var(--fl-muted-2)" }}>Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center flex flex-col items-center gap-2" style={{ color: "var(--fl-muted-2)" }}>
                <Bell size={20} />
                <div className="text-xs">You're all caught up.</div>
              </div>
            )}
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              const pc = PRIORITY[n.priority]?.color || "var(--fl-muted-2)";
              return (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className="w-full text-left p-3 flex gap-3 border-b transition-colors hover:bg-[color-mix(in_oklab,var(--fl-orange)_6%,transparent)]"
                  style={{ borderColor: "var(--fl-border)", background: n.read ? "transparent" : "color-mix(in oklab, var(--fl-orange) 4%, transparent)" }}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0"><Icon size={14} style={{ color: pc }} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate" style={{ color: "var(--fl-text)" }}>{n.title}</div>
                    {n.body && <div className="text-[11px] mt-0.5 line-clamp-2" style={{ color: "var(--fl-muted)" }}>{n.body}</div>}
                    <div className="text-[10px] mt-1" style={{ color: "var(--fl-muted-2)" }}>{timeAgo(n.created_at)} ago</div>
                  </div>
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: "var(--fl-orange)" }} />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
